import { useEffect, useRef, useState } from 'react';

import { api, ApiError, type Params } from '../api/client';
import { useApp } from '../state/app';

export interface Query<T> {
  data: T | undefined;
  error: Error | undefined;
  /** A request is in flight. `data` may still hold the previous window's answer. */
  loading: boolean;
}

/**
 * One GET, re-asked whenever the filters change or a write bumps the reload nonce. The
 * previous answer stays on screen while the next is fetched, so a filter change does not
 * collapse every card to a skeleton.
 */
export function useApi<T>(path: string, params: Params = {}, enabled = true): Query<T> {
  const { nonce, track } = useApp();
  const [data, setData] = useState<T>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(enabled);

  // Serialised, so a fresh object literal at the call site does not re-fire the effect.
  const key = JSON.stringify(params);
  // Held in a ref so `track` can be called symmetrically even if the effect tears down
  // before the request settles.
  const settled = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let live = true;
    settled.current = false;
    setLoading(true);
    track(1);

    const done = () => {
      if (settled.current) return;
      settled.current = true;
      track(-1);
    };

    api<T>(path, JSON.parse(key) as Params)
      .then((result) => {
        if (!live) return;
        setData(result);
        setError(undefined);
      })
      .catch((err: unknown) => {
        // A dead session is not a panel failure: the app root has already swapped in the
        // sign-in page, and an error box behind it would only be read as a second fault.
        if (err instanceof ApiError && err.status === 401) return;
        if (live) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        done();
        if (live) setLoading(false);
      });

    return () => {
      live = false;
      done();
    };
  }, [path, key, enabled, nonce, track]);

  return { data, error, loading };
}
